import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { closeProjectSidebar, toggleProjectSidebar } from "../../../../../actions/projectSidebar";
import { setShowTeamSidebar } from "../../../../../actions/teamSidebar";
import { resetTourToggle, setRun } from "../../../../../actions/tour";
import { setShowUserProfileSidebar } from "../../../../../actions/userProfileSidebar";
import { updateUserAvailabilityStatus } from "../../../../../actions/userProfile";
import { emailIntegrationScrollRedux } from "../../../../../actions/projectWorkflows";
import { useGetUser } from "../../../../../components/UserProfileSetting/hooks/userProfileHelper.hooks";
import { PROJECT_TYPE } from "../../../../../components/ProjectSidebar/projectSidebar.constants";
import { handleCloseSidebar } from "../../../../../components/Tasks/tasks.common";
import { icon, label, number, route, tooltip } from "../../../../../config";
import { getLiveTimezone, getWorkflowPanelUrl } from "../../../../../helper/common";
import { checkTargetFile } from "../../../../../utils";
import { copyTextToClipboard } from "../../../../../utils/common";
import { filterData } from "../../../../../utils/kendo";
import { getUserProfileControlsData } from "../../../../../shared/services/auth.service";
import SetAwayStatusModal from "../../UserDropdown/SetAwayStatusModal";
import ProfilePicturePopUp from "../ProfilePicturePopUp";
import { useProfileContext } from "../UserProfile.context";
import userDummy from "../../../../../assets/images/dummy-user.jpg";

/**
 * PersonalInformationSection component for Profile section
 * Displays personal information fields like name, code, board name, email
 * @author Bhavana
 */
const PersonalInformationSection = () => {
  const user = useGetUser();
  const history = useHistory();
  const { uId } = useParams();
  const dispatch = useDispatch();
  const { user: authUser } = useSelector((state) => state.auth);
  const taskSidebar = useSelector((state) => state.taskSidebar);
  const taskSidebarData = taskSidebar?.isNewTask;

  const [showProfilePicPopup, setShowProfilePicPopup] = useState(false);
  const [showAwayModal, setShowAwayModal] = useState(false);
  const [awayModalAnchor, setAwayModalAnchor] = useState(null);
  const awayModalAnchorRef = useRef(null);
  const userPenIconRef = useRef(null);
  const inputFileRef = useRef(null);
  const changePicAnchorRef = useRef(null);

  const {
    Name,
    email,
    JobTitle,
    projectCode,
    PhotoLinks,
    timezone,
    UserReminderTime,
    userDomainEmail,
    defaultWorkflowId,
    myProjectId,
    myProjectName,
  } = user;

  const {
    selectedImage,
    setSelectedImage,
    setSelectedImageFolder,
    displayName,
    setDisplayName,
    userEmail,
    setUserEmail,
    taskCode,
    setTaskCode,
    boardName,
    setBoardName,
    setProfileData,
    setIsEnableSaveButton,
    initialValues,
    setInitialValues,
    timeZoneList,
    timeZone,
    setTimeZone,
    setTimeZoneList,
    initialTimeZone,
  } = useProfileContext();

  const currentStatus = authUser?.availabilityStatus || label.AVAILABLE;
  const normalizedStatus = currentStatus?.toString().trim();
  const isAvailable = normalizedStatus === label.AVAILABLE;
  const isAway = normalizedStatus === label.AWAY;
  const tabNames = ["teams", "workflows", "forms", "automation"];
  const isTabName = uId && tabNames.includes(uId);
  const userIdMatch = uId && authUser?.id && (uId === authUser.id.toString() || uId === String(authUser.id) || Number(uId) === Number(authUser.id));
  const canUpdateStatus = !uId || isTabName || userIdMatch;

  /**
   * Update initial timezone value
   * @author Bhavana
   */
  useEffect(() => {
    if (timezone) {
      setTimeZone(timezone);
      setInitialValues((prev) => ({
        ...prev,
        timeZone: timezone,
        profileData: {
          ...prev.profileData,
          timezone: timezone,
        },
      }));
    }
  }, [authUser?.id]);


  /**
   * Initializes input values when the component renders.
   * @author Bhavana
   */
  useEffect(() => {
    if (user?.id) {
      setSelectedImage(PhotoLinks?.medium);
      setDisplayName(Name ?? "");
      setUserEmail(email ?? "");
      setTaskCode(projectCode ?? "");
      setBoardName(Name ?? "");

      const initialProfileData = {
        Name: Name ?? "",
        JobTitle: JobTitle ?? "",
        projectCode: projectCode ?? "",
        defaultReminderTime: UserReminderTime ?? "",
        timezone: timezone ?? "",
        PhotoLink: PhotoLinks?.medium ?? null,
        Manager: user.Manager ?? null,
        JobRole: user.JobRole ?? null,
        Department: user.Department ?? null,
        OfficeLocation: user.OfficeLocation ?? null,
      };

      setInitialValues((prev) => ({
        ...prev,
        displayName: Name ?? "",
        taskCode: projectCode ?? "",
        selectedImage: PhotoLinks?.medium ?? null,
        profileData: {
          ...prev.profileData,
          ...initialProfileData,
        },
      }));
    }
  }, [user]);

  /**
   * Handles input changes by updating corresponding state variables and the profileData object.
   * Enables save button if value is different from initial, disables if it matches initial.
   * @author Bhavana
   */
  const handleChange = (event) => {
    const { name, value } = event.target;
    let isDifferentFromInitial = false;

    switch (name) {
      case "displayName":
        setDisplayName(value);
        setBoardName(value);
        setProfileData((prevState) => ({
          ...prevState,
          Name: value,
        }));
        isDifferentFromInitial = value !== initialValues.displayName;
        break;

      case "taskCode":
        const upperCaseValue = value.toUpperCase();
        setTaskCode(upperCaseValue);
        setProfileData((prevState) => ({
          ...prevState,
          projectCode: upperCaseValue,
        }));
        isDifferentFromInitial = upperCaseValue !== initialValues.taskCode;
        break;
    }
    setIsEnableSaveButton(isDifferentFromInitial);
  };

  /**
   * Copies the user's domain email to the clipboard.
   * @param {Object} e - The event object.
   * @author Bhavana
   */
  const copyToClipboard = (e) => {
    e.stopPropagation();
    copyTextToClipboard(userDomainEmail, label.EMAIL_COPIED);
  };

  const workflowUrl = getWorkflowPanelUrl({
    projectId: myProjectId,
    projectName: myProjectName,
    workflowId: defaultWorkflowId,
    panelKey: "emailIntegration",
  });

  /**
   * Handles the input of a user profile picture, and updates the selectedImage state with the file URL.
   * @author Bhavana
   */
  const handleFileInput = (event) => {
    const targetFile = checkTargetFile(event);
    if (targetFile) {
      const fileURL = URL.createObjectURL(targetFile);
      setSelectedImage(fileURL);
      setSelectedImageFolder(targetFile);
      setProfileData((prevState) => ({
        ...prevState,
        PhotoLink: fileURL,
        fileContent: targetFile,
      }));
      const isDifferentFromInitial = fileURL !== initialValues.selectedImage;
      setIsEnableSaveButton(isDifferentFromInitial);
    } else {
      inputFileRef.current.value = "";
      setSelectedImage("");
    }
  };

  /**
   * Navigate the page to my-board and closes all the sidebar
   * @author Bhavana
   */
  const handleRestartTour = async () => {
    history.push(route.PRIVATE_ROUTE.LANDING_PAGE.PATH);
    await dispatch(toggleProjectSidebar(PROJECT_TYPE.PERSONAL_PROJECT, false));
    await handleCloseSidebar(dispatch, taskSidebarData);
    await dispatch(setShowTeamSidebar({ showQueueSidebar: false }));
    await dispatch(setShowUserProfileSidebar({ showUserProfileSidebar: false }));
    await closeProjectSidebar();
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await restartTour();
  };

  /**
   * Handles restarting the tour by navigating to the tasks route and dispatching necessary actions to start tour.
   * @author Bhavana
   */
  const restartTour = async () => {
    await dispatch(resetTourToggle(true));
    await dispatch(setRun(true));
  };

  /**
   * Removes image from the input field and resets profile image state.
   * @author Bhavana
   */
  const handleRemoveImage = (event) => {
    event?.preventDefault();
    if (inputFileRef?.current) inputFileRef.current.value = "";
    setSelectedImage("");
    setProfileData((prevState) => ({
      ...prevState,
      PhotoLink: null,
      fileContent: null,
    }));
    const isDifferentFromInitial = initialValues?.selectedImage !== null;
    setIsEnableSaveButton(isDifferentFromInitial);
  };

  /**
   * Toggles the profile picture popup visibility.
   * @author Bhavana
   */
  const handleShowProfilePicPopup = () => {
    setShowProfilePicPopup((prev) => !prev);
  };

  /**
   * Triggers the file input for changing profile picture.
   * @author Bhavana
   */
  const handleChangeProfilePicture = () => {
    inputFileRef?.current?.click();
  };

  /**
   * Fetches the live timezone and updates the timezone state in the organization data.
   * @author Bhavana
   */
  const handleLiveTimeZone = async () => {
    const liveTimezone = await getLiveTimezone();
    setTimeZone(liveTimezone);
    const isDifferentFromInitial = liveTimezone !== initialValues?.timeZone;
    setIsEnableSaveButton(isDifferentFromInitial);
  };

  /**
   * Filters timezone list based on name in timezone dropdown.
   * @author Bhavana
   */
  const filterChangeForTimezone = async (event) => {
    const filteredList = filterData(event?.filter, initialTimeZone);
    setTimeZoneList([...filteredList]);
  };

  /**
   * Handles user status change
   * @author Muskan Thakur
   */
  const handleStatusChange = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authUser?.id) {
      return;
    }
    if (!canUpdateStatus) {
      return;
    }

    const newStatus = isAvailable && !isAway ? label.AWAY : label.AVAILABLE;

    await dispatch(updateUserAvailabilityStatus(authUser.id, newStatus));
  };

  /**
   * Handles USER_PEN icon click to open status modal
   * @author Muskan Thakur
   */
  const handleUserPenIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAwayModalAnchor(userPenIconRef);
    setShowAwayModal(true);
  };

  /**
   * Handles modal close
   * @author Muskan Thakur
   */
  const handleModalClose = () => {
    setShowAwayModal(false);
    setAwayModalAnchor(null);
  };

  /**
   * Handles changes in the timezone dropdown and updates state.
   * @author Bhavana
   */
  const handleTimeZoneChange = (event) => {
    const { name, value } = event?.target;
    let isDifferentFromInitial = false;

    switch (name) {
      case "timeZone":
        setTimeZone(value);
        setProfileData((prevState) => ({
          ...prevState,
          timezone: value,
        }));
        isDifferentFromInitial = value !== initialValues?.timeZone;
        break;
      default:
        break;
    }
    setIsEnableSaveButton(isDifferentFromInitial);
  };

  /**
   * Handles the click event for the workflow link.
   * @author Bhavana
   */
  const handleLinkClick = () => {
    dispatch(emailIntegrationScrollRedux(true));
    dispatch(toggleProjectSidebar(PROJECT_TYPE.PERSONAL_PROJECT, false));
  };

  return (
    <div className="profile-section-content bg-light" id="profile-personal-info">
      <div className="row">
        {/* LeftProfilePanel Content - Profile Picture, Timezone, User Status, Tour */}
        <div className="col-md-12 text-center user-profile-avatar-upload object-fit-cover position-relative mx-auto mb-3 d-flex align-items-center">
          <div className="row">
            <div className="col-md-3 d-flex justify-content-center">
              <div className="profile-upload-placeholder">
                <div className="profile-upload-image-wrapper">
                  <img
                    src={selectedImage || userDummy}
                    alt="user"
                    className={`profile-user-avatar d-block object-fit-cover rounded-circle border-style rounded-circle light-grey mx-auto d-block${
                      !selectedImage ? " profile-user-avatar-dummy" : " uploaded-image-hover"
                    } ${currentStatus !== label.AVAILABLE ? " profile-picture-away" : ""}`}
                  />
                  <input hidden type="file" onChange={handleFileInput} ref={inputFileRef} id="userprofilesidebar-upload-image" disabled={uId} />
                  <label
                    ref={changePicAnchorRef}
                    htmlFor={!selectedImage ? "userprofilesidebar-upload-image" : undefined}
                    className={`update-profile-link-on-image cursor-pointer${selectedImage ? " uploaded-overlay" : ""} `}
                    onClick={selectedImage ? handleShowProfilePicPopup : undefined}>
                    {selectedImage ? (
                      <>
                        <div className="flex-column d-flex justify-content-center align-items-center mt-5">
                          <span>{icon.PERSON_ADD}</span>
                          <span>{label.CHANGE_PROFILE_PICTURE}</span>
                        </div>
                      </>
                    ) : (
                      label.UPDATE_PROFILE_PHOTO
                    )}
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-9">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex justify-content-between align-items-center w-100 mb-1">
                    <label className="mb-0">{label.TIME_ZONE}</label>
                    <span
                      className="pull-right cursor-pointer fs-1"
                      title={label.USE_LIVE_LOCATION}
                      onClick={handleLiveTimeZone}
                      id="userprofilesidebar-handle-live-timezone">
                      <Tooltip anchorElement="target" parentTitle={true} position="bottom">
                        {icon.LOCATION}
                      </Tooltip>
                    </span>
                  </div>
                  <div className="form-group d-flex">
                    <DropDownList
                      className="w-100"
                      data={timeZoneList}
                      value={timeZone}
                      name="timeZone"
                      onChange={handleTimeZoneChange}
                      filterable={true}
                      onFilterChange={filterChangeForTimezone}
                      id="userprofilesidebar-timezone-dropdown"
                      disabled={uId}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex justify-content-between align-items-center w-100 mb-1">
                    <label className="mb-0">{label.USER_STATUS}</label>
                    {canUpdateStatus && (
                      <Tooltip anchorElement="target" parentTitle={true} position="bottom">
                        <button
                          ref={userPenIconRef}
                          type="button"
                          className="btn btn-link p-0 border-0 bg-transparent cursor-pointer user-pen-icon-button d-flex"
                          onClick={handleUserPenIconClick}
                          title="Set a status">
                          {icon.USER_PEN}
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="form-group d-flex" ref={awayModalAnchorRef}>
                    <button
                      type="button"
                      className={`btn status-button ${isAvailable ? "btn-success" : "btn-tertiary"}`}
                      onClick={handleStatusChange}
                      disabled={!canUpdateStatus}
                      id="userprofilesidebar-user-status-button">
                      {currentStatus}
                    </button>
                  </div>
                </div>
                <div className="col-md-12 form-group mb-0">
                  <div className="quick-tour-btn-wrapper d-flex justify-content-center">
                    <button
                      className="quick-tour-btn w-100"
                      id="userprofilesidebar-restart-tour-btn"
                      onClick={handleRestartTour}
                      type="button"
                      disabled={uId}>
                      <span className="quick-tour-icon">{icon.QUICK_TOUR}</span>
                      <span className="quick-tour-text">{label.TAKE_TOUR}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {selectedImage && (
            <ProfilePicturePopUp
              anchor={changePicAnchorRef}
              show={showProfilePicPopup}
              handleFileInput={handleFileInput}
              onClose={handleShowProfilePicPopup}
              onChangePicture={handleChangeProfilePicture}
              onRemovePicture={handleRemoveImage}
            />
          )}
          <SetAwayStatusModal
            show={showAwayModal}
            onClose={handleModalClose}
            anchor={awayModalAnchor || awayModalAnchorRef}
          />
        </div>

        {/* RightProfilePanel Content - Personal Information Form */}
        <div className="col-md-12">
          <div className="p-3 bg-white">
            <h6 className="mb-3">{label.PERSONAL_INFOMATION}</h6>
            <div className="form-group d-flex">
              <div className="w-75">
                <label>{label.NAME}</label>
                <sup className="text-danger">*</sup>
                <Input
                  className="w-100"
                  placeholder=" Name"
                  disabled={uId}
                  name="displayName"
                  value={displayName}
                  onChange={handleChange}
                  maxLength={number.SIXTY}
                  id="userprofilesidebar-displayname"
                />
              </div>
              <div className="w-25 ml-2">
                <label>{label.CODE}</label>
                <sup className="text-danger">*</sup>
                <Input
                  className="w-100"
                  placeholder="My Task Code"
                  disabled={uId}
                  name="taskCode"
                  value={taskCode}
                  onChange={handleChange}
                  maxlength={number.TEN}
                  id="userprofilesidebar-task-code"
                />
              </div>
            </div>
            <div className="form-group">
              <label>{label.BOARD_NAME}</label>
              <Input
                className="w-100"
                placeholder="Personal Board Name"
                name="boardName"
                value={boardName}
                maxLength={number.THIRTY_FIVE}
                id="userprofilesidebar-personal-board"
                disabled={true}
              />
            </div>
            <div className="form-group">
              <label>{label.DRUTAS_EMAIL}</label>
              <div className="work-email-container d-flex justify-content-between p-2 border-radius-10">
                <Tooltip anchorElement="target" parentTitle={true} position="bottom">
                  <Link
                    to={workflowUrl}
                    onClick={() => {
                      handleLinkClick();
                      dispatch(setShowUserProfileSidebar({ showUserProfileSidebar: false }));
                    }}
                    rel="noopener noreferrer"
                    className="link-text cursor-pointer email-text black mt-1"
                    id="userprofilesidebar-user-domain-email"
                    title={label.CONFIGURE}>
                    {userDomainEmail}
                  </Link>
                </Tooltip>
                <Tooltip anchorElement="target" parentTitle={true} position="bottom">
                  <div className="work-email-icons d-flex align-items-center">
                    <span
                      id="userprofilesidebar-workflow-edit-email-btn"
                      title={label.CONFIGURE}
                      className="cursor-pointer mb-1 icon-size-20"
                      onClick={() => {
                        handleLinkClick();
                        dispatch(setShowUserProfileSidebar({ showUserProfileSidebar: false }));
                        history.push(workflowUrl);
                      }}>
                      {icon.EDIT_EMAIL}
                    </span>
                    <span
                      id="userprofilesidebar-workflow-copy-email-btn"
                      title={tooltip.COPY_EMAIL}
                      className="cursor-pointer ack-edit-button-icon align-items-center ml-2 mb-1 icon-size-20"
                      onClick={(e) => copyToClipboard(e)}>
                      {icon.COPY_CONTENT}
                    </span>
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInformationSection;

